import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { followTeam, unfollowTeam, fetchFollowedTeams } from "../../../features/scout/scoutProfileSlice";
import teamLogo from "../../../assets/images/coachTeamLogo.png";
import { Pill } from "./components/Pill";
import { CollegeDropdown } from "./components/CollegeDropdown";
import { AllCoachesModal } from "../../../components/common/AllCoachesModal";
import { formatLocation } from "../../../utils/formatLocation";

export const CoachTeamHeader = ({
  selectedCollege,
  colleges,
  onSelectCollege,
  enableNavigation = false,
  onSearch,
  onLoadMore,
  hasMore = false,
  loadingMore = false,
  loading = false,
  onDropdownOpen
}) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [showCoachesModal, setShowCoachesModal] = useState(false);
  const teamLogoSrc = selectedCollege?.logo || teamLogo;
  const [followLoading, setFollowLoading] = useState(false);
  const [isFollowing, setIsFollowing] = useState(false);

  // Get coach from coachInfo array - prioritize Head Coach
  const coachInfo = selectedCollege?.coachInfo || [];
  // Find head coach (title contains "Head"), otherwise use first coach
  const headCoach = coachInfo.find(coach =>
    coach.title?.toLowerCase().includes('head')
  );
  const firstCoach = headCoach || coachInfo[0] || null;
  const hasMultipleCoaches = coachInfo.length > 1;

  // Use first coach data or fallback to legacy fields
  const displayCoachName = firstCoach?.name || selectedCollege?.coachName;
  const displayCoachTitle = firstCoach?.title || selectedCollege?.coachPosition;
  const displayCoachPhone = firstCoach?.phone || selectedCollege?.coachPhoneNumber;
  const displayCoachEmail = firstCoach?.email || selectedCollege?.coachEmail;

  // Get followed teams from Redux
  const { followedTeams } = useSelector((state) => state.scoutProfile);

  // Fetch followed teams on mount
  useEffect(() => {
    dispatch(fetchFollowedTeams({ page: 1, limit: 100 }));
  }, [dispatch]);

  // Check if current team is in followed teams list
  useEffect(() => {
    if (selectedCollege?._id && followedTeams && followedTeams.length > 0) {
      const isTeamFollowed = followedTeams.some(
        (team) => team._id === selectedCollege._id || team.id === selectedCollege._id
      );
      setIsFollowing(isTeamFollowed);
    } else {
      setIsFollowing(false);
    }
  }, [selectedCollege, followedTeams]);

  const handleLogoClick = () => {
    if (enableNavigation && selectedCollege?._id) {
      navigate(`/scout/team/${selectedCollege._id}`, {
        state: {
          teamData: selectedCollege
        }
      });
    }
  };

  const handleFollowToggle = async () => {
    if (!selectedCollege?._id || followLoading) return;

    setFollowLoading(true);
    try {
      if (isFollowing) {
        await dispatch(unfollowTeam(selectedCollege._id)).unwrap();
        setIsFollowing(false);
      } else {
        await dispatch(followTeam(selectedCollege._id)).unwrap();
        setIsFollowing(true);
      }
      dispatch(fetchFollowedTeams({ page: 1, limit: 100 }));
    } catch (error) {
      console.error('Failed to toggle team follow:', error);
    } finally {
      setFollowLoading(false);
    }
  };

  return (
    <div>
      {/* Coach Info & Follow/Unfollow Button */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-4">
        {/* Coach Contact Info - Left Aligned */}
        {(displayCoachName || displayCoachPhone || displayCoachEmail) && (
          <div className="flex flex-wrap items-center gap-3 sm:gap-4 text-sm">
            {displayCoachName && (
              <div className="flex items-start gap-2 text-slate-300">
                <svg className="w-4 h-4 flex-shrink-0 text-cyan-400 mt-0.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
                  <circle cx="12" cy="7" r="4"/>
                </svg>
                <div className="flex flex-col">
                  <span className="font-medium">{displayCoachName}</span>
                </div>
              </div>
            )}
            {displayCoachPhone && (
              <a
                href={`tel:${displayCoachPhone}`}
                className="flex items-center gap-2 text-slate-300 hover:text-cyan-400 transition-colors"
              >
                <svg className="w-4 h-4 flex-shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"/>
                </svg>
                <span>{displayCoachPhone}</span>
              </a>
            )}
            {displayCoachEmail && (
              <a
                href={`mailto:${displayCoachEmail}`}
                className="flex items-center gap-2 text-slate-300 hover:text-cyan-400 transition-colors"
              >
                <svg className="w-4 h-4 flex-shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
                  <polyline points="22,6 12,13 2,6"/>
                </svg>
                <span>{displayCoachEmail}</span>
              </a>
            )}
            {/* View All Coaches Button */}
            {hasMultipleCoaches && (
              <button
                onClick={() => setShowCoachesModal(true)}
                className="flex items-center gap-2 px-3 py-1.5 bg-cyan-500/10 text-cyan-400 rounded-lg hover:bg-cyan-500/20 transition-colors text-sm font-medium"
              >
                <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
                  <circle cx="9" cy="7" r="4"/>
                  <path d="M23 21v-2a4 4 0 0 0-3-3.87"/>
                  <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
                </svg>
                View All Coaches ({coachInfo.length})
              </button>
            )}
          </div>
        )}

        <button
          onClick={handleFollowToggle}
          disabled={followLoading}
          className={`${
            isFollowing
              ? 'bg-gray-600 hover:bg-gray-700'
              : 'bg-cyan-500 hover:bg-cyan-600'
          } text-white px-6 py-2.5 rounded-lg font-semibold transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2`}
        >
          {followLoading ? (
            <>
              <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
              <span>{isFollowing ? 'Unfollowing...' : 'Following...'}</span>
            </>
          ) : (
            <>
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                {isFollowing ? (
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M5 13l4 4L19 7"
                  />
                ) : (
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M12 4v16m8-8H4"
                  />
                )}
              </svg>
              <span>{isFollowing ? 'Unfollow Team' : 'Follow Team'}</span>
            </>
          )}
        </button>
      </div>

      {/* Original Header Content */}
      <div className="flex items-center gap-6 pb-4 border-b border-white/10">
        {/* Logo */}
        <div className="shrink-0 flex flex-col items-start">
          <img
            src={teamLogoSrc}
            alt="team"
            className={`w-32 h-32 object-contain ${enableNavigation ? 'cursor-pointer hover:opacity-80 transition-opacity' : ''}`}
            onError={(e) => {
              e.target.src = teamLogo;
            }}
          />
          <div className="mt-2">
            <CollegeDropdown
              selectedCollege={selectedCollege}
              colleges={colleges}
              onSelectCollege={onSelectCollege}
              onSearch={onSearch}
              onLoadMore={onLoadMore}
              hasMore={hasMore}
              loadingMore={loadingMore}
              loading={loading}
              onDropdownOpen={onDropdownOpen}
            />
          </div>
        </div>

        {/* Info pills */}
        <div className="flex-1">
          <div className="grid grid-cols-3 xl:grid-cols-5 gap-y-3 gap-x-4">
            {/* Top Row - Team Info */}
            <Pill label="Division" value={selectedCollege?.divisionName || selectedCollege?.team?.divisionName || selectedCollege?.division || "N/A"} />
            <Pill label="Region" value={selectedCollege?.region || "N/A"} />
            <Pill label="Conference" value={selectedCollege?.conference || "N/A"} />
            <Pill label="Location" value={
              formatLocation(selectedCollege?.teamLocation || selectedCollege?.location)
              || (selectedCollege?.town && selectedCollege?.state ? `${selectedCollege.town}, ${selectedCollege.state}` : null)
              || selectedCollege?.town
              || selectedCollege?.state
              || "N/A"
            } />
            <Pill label="Rank" value={selectedCollege?.rank || "N/A"} />

            {/* Second Row - Record Section */}
            <Pill label="Overall Record" value={
              selectedCollege?.record
              || selectedCollege?.overallRecord
              || (selectedCollege?.wins != null && selectedCollege?.losses != null
                  ? `${selectedCollege.wins}-${selectedCollege.losses}${selectedCollege.ties ? `-${selectedCollege.ties}` : ''}`
                  : "N/A")
            } />
            <Pill label="Home" value={selectedCollege?.homeRecord || selectedCollege?.home || "N/A"} />
            <Pill label="Away" value={selectedCollege?.awayRecord || selectedCollege?.away || "N/A"} />
            <Pill label="Neutral" value={selectedCollege?.neutralRecord || selectedCollege?.neutral || "N/A"} />
            <Pill label="Conference Record" value={selectedCollege?.conferenceRecord || "N/A"} />
          </div>
        </div>
      </div>

      {/* All Coaches Modal */}
      <AllCoachesModal
        isOpen={showCoachesModal}
        onClose={() => setShowCoachesModal(false)}
        coaches={coachInfo}
        teamName={selectedCollege?.name || selectedCollege?.collegeName}
      />
    </div>
  );
};
